// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/date-formatting";
import {
  CustomFieldDataType,
  type CustomFieldDefinition,
} from "@/types/custom-fields";
import type { UnifiedTableColumn } from "@/types/table-column";

/**
 * Generate a table column configuration for a custom field
 *
 * @param field The custom field definition
 * @returns A column configuration object
 */
export function createCustomFieldColumn(
  field: CustomFieldDefinition,
): UnifiedTableColumn<Record<string, unknown>> {
  const dataType = field.data_type;
  // Base column properties – use the cleaned type for simple checks
  const column: UnifiedTableColumn<Record<string, unknown>> = {
    key: field.id,
    title: field.display_name,
    sortable: true,
    filterable: true,
    defaultSortDirection: "asc",
    nullSort: "last",
    isCustomField: true,
  } as unknown as UnifiedTableColumn<Record<string, unknown>>;

  // Store original type for reference
  column.customFieldDataType = dataType;

  switch (dataType) {
    case CustomFieldDataType.TEXT:
      // If we have an explicit list of allowed values, use a select filter instead
      if (Array.isArray(field.value_enum) && field.value_enum.length > 0) {
        column.filterType = "select";
        column.filterOptions = field.value_enum.map((v) => ({
          id: v,
          label: v,
        }));
      } else {
        column.filterType = "text";
      }
      column.dataType = "TEXT";
      column.render = (record) => String(record[field.id] ?? "-");
      break;

    case CustomFieldDataType.NUMBER:
      column.filterType = "number";
      column.dataType = "NUMBER";
      column.defaultSortDirection = "desc";
      column.render = (record) => {
        const value = record[field.id];
        if (value === null || value === undefined) return "-";
        return typeof value === "number"
          ? value.toLocaleString()
          : String(value);
      };
      break;

    case CustomFieldDataType.BOOLEAN:
      column.filterType = "select";
      column.dataType = "BOOLEAN";
      column.filterOptions = [
        { id: "true", label: "Yes" },
        { id: "false", label: "No" },
      ];
      column.render = (record) => {
        const value = record[field.id];
        if (value === null || value === undefined) return "-";
        return value ? "Yes" : "No";
      };
      break;

    case CustomFieldDataType.DATE:
    case CustomFieldDataType.DATETIME:
      column.filterType = "date";
      column.dataType = dataType;
      column.render = (record) => {
        const value = record[field.id];
        if (value === null || value === undefined) return "-";
        return formatDate(String(value));
      };
      column.exportValue = (record) => {
        const value = record[field.id];
        if (value === null || value === undefined) return "-";
        return formatDate(String(value));
      };
      break;

    case CustomFieldDataType.ARRAY:
      // Treat as array of strings – display as pills and allow select filtering
      column.filterType = "select";
      column.dataType = "ARRAY";

      if (Array.isArray(field.value_enum) && field.value_enum.length > 0) {
        column.filterOptions = field.value_enum.map((v) => ({
          id: v,
          label: v,
        }));
      }

      column.render = (record) => {
        const raw = record[field.id];

        // Support both array and delimited string fallback
        const arr: string[] | null = Array.isArray(raw)
          ? (raw as string[])
          : typeof raw === "string" && raw
            ? raw
                .split(";")
                .map((s) => s.trim())
                .filter(Boolean)
            : null;

        if (!arr || arr.length === 0) return "-";

        return (
          <div className="flex flex-wrap gap-1">
            {arr.map((val, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {val}
              </Badge>
            ))}
          </div>
        );
      };
      break;

    default:
      // Unknown types fall back to a basic text filter
      if (Array.isArray(field.value_enum) && field.value_enum.length > 0) {
        column.filterType = "select";
        column.filterOptions = field.value_enum.map((v) => ({
          id: v,
          label: v,
        }));
      } else {
        column.filterType = "text";
      }
      column.dataType = dataType;
      column.render = (record) => String(record[field.id] ?? "-");
  }

  return column;
}

/**
 * Creates table columns for multiple custom fields
 *
 * @param fields Array of custom field definitions
 * @returns Array of column configurations
 */
export function createCustomFieldColumns(
  fields: CustomFieldDefinition[],
): UnifiedTableColumn<Record<string, unknown>>[] {
  return fields.map(createCustomFieldColumn);
}
